import { MAX_ENTITIES } from "../constants.ts";
import { SLOT_COUNT } from "../heroes/types.ts";

/**
 * Structure-of-arrays component storage, indexed by bitECS entity id.
 * All timers are *remaining ticks* countdowns so predicted client state is independent
 * of absolute server tick numbers (required for reconciliation).
 */
export const HERO_VARS = 16;
export const abilIdx = (eid: number, slot: number) => eid * SLOT_COUNT + slot;

export function createStores() {
  const f32 = () => new Float32Array(MAX_ENTITIES);
  const i32 = () => new Int32Array(MAX_ENTITIES);
  const u8 = () => new Uint8Array(MAX_ENTITIES);

  const Pos = { x: f32(), y: f32(), z: f32() };
  /** Controlled velocity (horizontal) + vertical velocity. */
  const Vel = { x: f32(), y: f32(), z: f32() };
  /** External horizontal velocity from knockback; decays over time. */
  const Ext = { x: f32(), z: f32() };
  const Look = { yaw: f32(), pitch: f32() };
  const Net = { id: new Uint16Array(MAX_ENTITIES) };

  const Player = {
    hero: u8(),
    /** Current form for the active/visible hero. Form 0 is the base definition. */
    form: u8(),
    /** The real roster hero while disguised; equals hero when not disguised. */
    baseHero: u8(),
    /** Remaining ticks for a temporary disguise. */
    disguiseTicks: i32(),
    team: u8(),
    alive: u8(),
    grounded: u8(),
    crouch: u8(),
    respawn: i32(),
    /** Previous tick's buttons, for press-edge detection (part of predicted state). */
    buttons: new Uint16Array(MAX_ENTITIES),
    /** Targeting (see sim/targeting.ts): slot + 1 being aimed (0 = none) and ticks spent aiming. */
    aimSlot: u8(),
    aimTicks: i32(),
    /** Fire buttons that confirmed or cancelled targeting, ignored until they're released. */
    aimMask: u8(),
    /** Slot + 1 whose targeting confirmed this tick (read by the kit during the same tick). */
    aimDone: u8(),
  };

  const Health = {
    hp: f32(),
    maxHp: f32(),
    armor: f32(),
    maxArmor: f32(),
    shield: f32(),
    maxShield: f32(),
    over: f32(),
    overTicks: i32(),
    sinceDamage: i32(),
  };

  const Status = {
    stun: i32(),
    root: i32(),
    slow: i32(),
    slowPct: f32(),
    sleep: i32(),
    invuln: i32(),
    knockdown: i32(),
    /** Damage dealt +boostPct. */
    boost: i32(),
    boostPct: f32(),
    phased: i32(),
    /** Healing received -antiHealPct (1 = none gets through). */
    antiHeal: i32(),
    antiHealPct: f32(),
    /** Healing received +healBoostPct. */
    healBoost: i32(),
    healBoostPct: f32(),
    /** Damage taken -resistPct. */
    resist: i32(),
    resistPct: f32(),
    /** Soul-linked: linkedPct of damage to one linked enemy echoes to the others. */
    linked: i32(),
    linkedPct: f32(),
    stealth: i32(),
    haste: i32(),
    hastePct: f32(),
    unstoppable: i32(),
    marked: i32(),
    markedBy: i32(),
    bonded: i32(),
    bondedBy: i32(),
    frozen: i32(),
    /** Remaining held ticks. */
    held: i32(),
    /** Holder eid + 1 (0 = none). */
    heldBy: i32(),
  };

  /** Per-slot ability state: cooldown ticks remaining and charges available. */
  const Abil = {
    cd: new Float32Array(MAX_ENTITIES * SLOT_COUNT),
    charges: new Uint8Array(MAX_ENTITIES * SLOT_COUNT),
  };

  const Weapon = {
    ammo: f32(),
    reload: i32(),
    fireCd: i32(),
    /** Short-lived animation/action cue (see ActionKind) for remote rendering. */
    action: u8(),
    actionTicks: i32(),
    /** Incrementing shot counter; seeds deterministic spread. */
    shots: i32(),
    /** Weapon in hand for heroes with `def.weapons`, and the other one's magazine. */
    slot: u8(),
    stowed: f32(),
  };

  const Ult = { charge: f32(), active: i32() };

  /** Hero-specific scalar state (fuel, beam target, barrier hp…). Meaning per hero in kits. */
  const HeroVars = new Float32Array(MAX_ENTITIES * HERO_VARS);

  /** Scripted movement (dash, charge, grapple, leap). Overrides normal movement while ticks > 0. */
  const Move = {
    kind: new Uint16Array(MAX_ENTITIES),
    ticks: i32(),
    x: f32(),
    y: f32(),
    z: f32(),
    speed: f32(),
    /** Target entity id + 1, if any. */
    target: i32(),
    /** Set by movement when the last scripted move was obstructed (e.g. charge hit a wall). */
    blocked: u8(),
  };

  const Projectile = {
    kind: new Uint16Array(MAX_ENTITIES),
    owner: i32(),
    team: u8(),
    ticks: i32(),
    gravity: f32(),
    radius: f32(),
    target: i32(),
    /** Owner's input sequence at spawn; lets the owner's client match its predicted projectile. */
    seq: new Uint16Array(MAX_ENTITIES),
    bounces: u8(),
  };

  const Zone = {
    kind: u8(),
    owner: i32(),
    team: u8(),
    radius: f32(),
    ticks: i32(),
    age: i32(),
    /** Partner zone eid + 1 (portals). */
    link: i32(),
  };

  const Construct = {
    kind: new Uint16Array(MAX_ENTITIES),
    owner: i32(),
    team: u8(),
    hp: f32(),
    maxHp: f32(),
    ticks: i32(),
    age: i32(),
    yaw: f32(),
    v0: new Uint16Array(MAX_ENTITIES),
    flags: u8(),
  };

  /** Tag components for bitECS queries. */
  const IsPlayer = {};
  const IsProjectile = {};
  const IsZone = {};
  const IsConstruct = {};


  return {
    Pos, Vel, Ext, Look, Net, Player, Health, Status, Abil, Weapon, Ult, HeroVars, Move, Projectile, Zone, Construct,
    IsPlayer, IsProjectile, IsZone, IsConstruct,
  };
}

export type Stores = ReturnType<typeof createStores>;

export const hv = (c: Stores, eid: number, i: number) => c.HeroVars[eid * HERO_VARS + i]!;
export const setHv = (c: Stores, eid: number, i: number, v: number) => {
  c.HeroVars[eid * HERO_VARS + i] = v;
};

export enum MoveKind {
  None = 0,
  Dash = 1,
  Charge = 2,
  Grapple = 3,
  Leap = 4,
  Launch = 5,
  Pulled = 6,
  Swing = 7,
  Flight = 8,
  Hero = 16,
}

export enum ActionKind {
  None = 0,
  Fire = 1,
  AltFire = 2,
  Melee = 3,
  Cast = 4,
  Ult = 5,
  Reload = 6,
}

/** Resets all per-entity component values (entity ids are recycled). */
export function clearEntity(c: Stores, eid: number) {
  for (const store of [c.Pos, c.Vel, c.Ext, c.Look, c.Player, c.Health, c.Status, c.Weapon, c.Ult, c.Move, c.Projectile, c.Zone, c.Construct, c.Net]) {
    for (const arr of Object.values(store)) (arr as Float32Array)[eid] = 0;
  }
  c.Abil.cd.fill(0, eid * SLOT_COUNT, (eid + 1) * SLOT_COUNT);
  c.Abil.charges.fill(0, eid * SLOT_COUNT, (eid + 1) * SLOT_COUNT);
  c.HeroVars.fill(0, eid * HERO_VARS, (eid + 1) * HERO_VARS);
}
